import PaymentProcessorFactory from '@spinnaker/iliad/dist/utils/payments/PaymentProcessorFactory'
import { PaymentProcessorClasses } from '@spinnaker/iliad/dist/utils/payments/PaymentProcessor'
import { StripePaymentProcessorTypes, StripePaymentProcessor } from '@spinnaker/iliad/dist/utils/payments/StripePaymentProcessor'
import Logger from 'customer-flows/utils/logger'

/** URL for the Stripe JavaScript SDK */
const STRIPE_API_SCRIPT_URL = 'https://js.stripe.com/v3/'

/**
 * Configuration interface for Stripe processor initialization
 */
interface StripeConfig {
  /** Stripe publishable key (pk_test_... or pk_live_...) */
  publishableKey: string
  /** Type of Stripe processor to create */
  type: string
  /** Stripe Connect account ID for multi-party payments */
  accountId?: string
  /** Instance type to differentiate between different use cases (e.g., 'express-checkout', 'payment-form') */
  instanceType?: string
}

/**
 * Singleton manager for Stripe payment processors.
 *
 * This class manages the lifecycle of Stripe payment processors, ensuring that:
 * - Only one processor instance exists per unique configuration
 * - Processors are properly cached and reused
 * - Stripe SDK is loaded before processor initialization
 * - Different instance types can coexist (e.g., express checkout vs payment forms)
 *
 * @example
 * ```typescript
 * const manager = stripeProcessorManager
 * const processor = await manager.getProcessor({
 *   publishableKey: 'pk_test_123',
 *   type: 'STRIPE_INTENTS',
 *   accountId: 'acct_123',
 *   instanceType: 'express-checkout'
 * })
 * ```
 */
class StripeProcessorManager {
  private static instance: StripeProcessorManager
  /** Cache of processor promises keyed by configuration */
  private processors: Map<string, Promise<StripePaymentProcessor>> = new Map()

  /**
   * Gets the singleton instance of the StripeProcessorManager.
   *
   * @returns The singleton StripeProcessorManager instance
   */
  static getInstance(): StripeProcessorManager {
    if (!StripeProcessorManager.instance) {
      StripeProcessorManager.instance = new StripeProcessorManager()
    }
    return StripeProcessorManager.instance
  }

  /**
   * Generates a unique key for caching processors based on configuration.
   *
   * The key includes publishable key, processor type, account ID, and instance type
   * to ensure different configurations get separate processor instances.
   *
   * @param config - The Stripe configuration
   * @returns A unique string key for the configuration
   * @private
   */
  private getProcessorKey(config: StripeConfig): string {
    return `${config.publishableKey}-${config.type}-${config.accountId || 'no-account'}-${config.instanceType || 'default'}`
  }

  /**
   * Gets or creates a Stripe payment processor for the given configuration.
   *
   * This method implements caching to ensure that processors with identical
   * configurations are reused. If a processor for the given configuration
   * already exists, it returns the cached instance. Otherwise, it creates
   * a new processor and caches it for future use.
   *
   * @param config - Configuration for the Stripe processor
   * @returns Promise that resolves to a configured StripePaymentProcessor
   *
   * @example
   * ```typescript
   * const processor = await manager.getProcessor({
   *   publishableKey: 'pk_test_123',
   *   type: 'STRIPE',
   *   instanceType: 'express-checkout'
   * })
   * ```
   */
  async getProcessor(config: StripeConfig): Promise<StripePaymentProcessor> {
    const key = this.getProcessorKey(config)

    if (this.processors.has(key)) {
      return this.processors.get(key)!
    }

    const newProcessor = this.initializeProcessor(config)
    this.processors.set(key, newProcessor)

    return newProcessor
  }

  /**
   * Initializes a new Stripe payment processor with the given configuration.
   *
   * This method handles the complex initialization process including:
   * - Ensuring Stripe SDK is loaded
   * - Configuring processor options based on type
   * - Creating the appropriate processor instance (express checkout vs standard)
   * - Handling account ID filtering for different processor types
   *
   * @param config - Configuration for the processor
   * @returns Promise that resolves to an initialized StripePaymentProcessor
   * @throws Error if processor initialization fails
   * @private
   */
  private async initializeProcessor(config: StripeConfig): Promise<StripePaymentProcessor> {
    try {
      const { publishableKey, type, accountId, instanceType } = config

      if (!type) {
        throw new Error('Payment processor type is required but not available')
      }

      // Only include stripeAccount for STRIPE_INTENTS, not for STRIPE_CUSTOM_CONNECT or STRIPE
      const shouldIncludeAccountId = type === StripePaymentProcessorTypes.STRIPE_INTENTS
      const options = shouldIncludeAccountId && accountId ? { stripeAccount: accountId } : {}

      await this.waitForStripe()

      let processor: StripePaymentProcessor

      if (instanceType === 'express-checkout') {
        processor = new StripePaymentProcessor()
        await processor.fetchProcessor(
          type as StripePaymentProcessorTypes,
          publishableKey,
          options,
          () => { }
        )
      } else {
        processor = await PaymentProcessorFactory.createProcessor(
          PaymentProcessorClasses.STRIPE,
          type as StripePaymentProcessorTypes,
          publishableKey,
          options,
          () => { }
        )
      }

      return processor
    } catch (error) {
      Logger.error('Error initializing Stripe processor:', { error })
      throw error
    }
  }

  /**
   * Ensures the Stripe JavaScript SDK is loaded before proceeding.
   *
   * This method handles loading the Stripe SDK script if it's not already present.
   * It uses a polling mechanism to check for Stripe availability and includes
   * timeout handling to prevent infinite waiting.
   *
   * The method will:
   * - Check if Stripe is already available on the window object
   * - Load the Stripe script if not already present
   * - Poll for Stripe availability with a maximum number of attempts
   * - Handle script loading errors and timeouts
   *
   * @returns Promise that resolves when Stripe SDK is available
   * @throws Error if Stripe SDK fails to load or times out
   * @private
   */
  private async waitForStripe(): Promise<void> {
    return new Promise((resolve, reject) => {
      const maxAttempts = 100
      let attempts = 0

      const checkStripe = () => {
        attempts++

        if (window && typeof (window as any).Stripe === 'function') {
          Logger.info('Stripe is available')
          resolve()
          return
        }

        if (attempts === 1) {
          const existingScript = document.querySelector(`script[src=""]`)

          if (!existingScript) {
            const script = document.createElement('script')
            script.setAttribute('async', '')
            script.src = STRIPE_API_SCRIPT_URL
            script.onload = () => {
              Logger.info('Stripe script loaded')
            }
            script.onerror = () => {
              Logger.error('Failed to load Stripe script')
              reject(new Error('Failed to load Stripe script'))
            }

            try {
              document.head.appendChild(script)
            } catch (error) {
              Logger.error('Error appending Stripe script to document.head:', { error })
              reject(new Error('Error appending Stripe script to document.head'))
              return
            }
          }
        }

        if (attempts >= maxAttempts) {
          Logger.error('Timeout waiting for Stripe to load')
          reject(new Error('Timeout waiting for Stripe to load'))
          return
        }

        setTimeout(checkStripe, 100)
      }

      checkStripe()
    })
  }

  /**
   * Clears all cached processors.
   *
   * This method is primarily useful for testing scenarios where you need
   * to reset the processor cache, or for cleanup when processors are no
   * longer needed. All cached processor instances will be removed, forcing
   * new processors to be created on subsequent calls to getProcessor().
   *
   * @example
   * ```typescript
   * // Clear cache in tests
   * stripeProcessorManager.clearProcessors()
   *
   * // Clear cache for cleanup
   * stripeProcessorManager.clearProcessors()
   * ```
   */
  clearProcessors(): void {
    this.processors.clear()
  }
}

/**
 * Singleton instance of the StripeProcessorManager.
 *
 * This is the main export that should be used throughout the application
 * to manage Stripe payment processors.
 *
 * @example
 * ```typescript
 * import stripeProcessorManager from 'customer-flows/utils/stripe-processor-manager'
 *
 * const processor = await stripeProcessorManager.getProcessor({
 *   publishableKey: 'pk_test_123',
 *   type: 'STRIPE',
 *   instanceType: 'express-checkout'
 * })
 * ```
 */
export default StripeProcessorManager.getInstance()
